import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type State = 'exchanging' | 'error';

/**
 * The supabase client is created with `detectSessionInUrl: true`, which
 * automatically calls `exchangeCodeForSession` once at init when the URL
 * contains `?code=...`. We don't call it manually — doing so would race
 * with the auto-exchange (and StrictMode's double-mount in dev would burn
 * the PKCE verifier on the second pass).
 *
 * Instead we wait for the session to materialise via getSession /
 * onAuthStateChange, then redirect.
 */
export default function Callback() {
  const [state, setState] = useState<State>('exchanging');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const goToDashboard = () => {
      if (cancelled) return;
      navigate('/dashboard', { replace: true });
    };

    // If the auto-exchange already completed by the time we mount, this catches it.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data.session) goToDashboard();
    });

    // Otherwise wait for the SIGNED_IN event detectSessionInUrl will fire.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) goToDashboard();
    });

    // Fallback: if neither happens within a few seconds, surface the error.
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      supabase.auth.getSession().then(({ data }) => {
        if (cancelled || data.session) return;
        setState('error');
      });
    }, 6000);

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
      window.clearTimeout(timer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        {state === 'exchanging' && (
          <p className="text-sm text-gray-500">Đang xác nhận đăng nhập…</p>
        )}
        {state === 'error' && (
          <div>
            <p className="text-3xl mb-2">⚠️</p>
            <p className="text-sm text-red-700 mb-3">Liên kết không hợp lệ hoặc đã hết hạn.</p>
            <a href="/login" className="text-sm text-red-700 underline">
              Quay lại đăng nhập
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
